import { useState, useCallback } from 'react';
import { Alert } from 'react-native';
import { ContactData, SingleMessageData, BulkMessageData, SendResult, MessageLimits } from '../types/messageForm.types';
import { apiService } from '../../../services/api';
import { useAppStore } from '../../../store/appStore';

/**
 * Custom hook for message sending logic
 */
export const useMessageSending = () => {
  const { currentBusiness, currentSubscription, user } = useAppStore();
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<SendResult[]>([]);

  /**
   * Check message limits
   */
  const checkMessageLimits = useCallback(async (): Promise<MessageLimits | null> => {
    try {
      const response = await apiService.checkWhatsAppLimits();
      if (response.statusCode === 1000 && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error checking message limits:', error);
      return null;
    }
  }, []);

  /**
   * Get WhatsApp credentials from subscription
   */
  const getWhatsAppCredentials = useCallback(() => {
    if (!currentSubscription?.services?.wabu) {
      return null;
    }

    const wabuService = currentSubscription.services.wabu as any;
    const whatsappPhone = wabuService?.metadata?.whatsapp_phone?.[0];

    if (!whatsappPhone) {
      return null;
    }

    const phoneId = whatsappPhone.whatsapp_phone_number_id || 
                   whatsappPhone.phone_id || 
                   whatsappPhone.id || 
                   whatsappPhone.phone_number_id;

    return {
      phoneId,
      accessToken: whatsappPhone.access_token,
      businessAccountId: whatsappPhone.business_account_id,
    };
  }, [currentSubscription]);

  /**
   * Send single message
   */
  const sendSingleMessage = useCallback(async (data: SingleMessageData): Promise<SendResult> => {
    try {
      setIsSending(true);
      setProgress(0);

      // Check limits first
      const limits = await checkMessageLimits();
      if (limits && !limits.canSend) {
        throw new Error(limits.message);
      }

      // Get WhatsApp credentials
      const credentials = getWhatsAppCredentials();
      if (!credentials) {
        throw new Error('WhatsApp not configured for this business');
      }

      // Send template message (opening conversation)
      const templateParams = [
        currentBusiness?.name || 'DISLIX',
        data.message
      ];

      const response = await apiService.sendWhatsAppTemplateMessage({
        to: data.contact.phoneNumber,
        templateName: 'open_window_request',
        businessPhoneNumberId: credentials.phoneId,
        parameters: templateParams,
        businessId: currentBusiness?._id,
        contactName: data.contact.name,
        userId: user?.id,
        accessToken: credentials.accessToken,
        businessAccountId: credentials.businessAccountId,
        contactId: data.contact.existingContactId,
      });

      if (response.statusCode === 1000) {
        setProgress(100);
        return {
          contact: data.contact,
          success: true,
          messageId: response.data?.messages?.[0]?.id,
        };
      } else {
        throw new Error(response.errors?.[0]?.message || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error sending single message:', error);
      return {
        contact: data.contact,
        success: false,
        error: error.message || 'Failed to send message',
      };
    } finally {
      setIsSending(false);
      setProgress(0);
    }
  }, [currentBusiness, user, checkMessageLimits, getWhatsAppCredentials]);

  /**
   * Send bulk messages
   */
  const sendBulkMessages = useCallback(async (data: BulkMessageData): Promise<SendResult[]> => {
    try {
      setIsSending(true);
      setProgress(0);
      setResults([]);

      // Check limits first - only block if completely at limit (no quota available)
      // If user has some quota remaining, let backend handle limiting contacts
      const limits = await checkMessageLimits();
      if (limits && !limits.canSend) {
        throw new Error(limits.message);
      }

      // Don't check if we have enough quota for all contacts here
      // Backend will automatically limit contacts to available quota
      // This allows better UX: user can add more contacts, backend sends what it can

      // Prepare contacts for task
      const contacts = data.contacts.map(contact => ({
        name: contact.name,
        phoneNumber: contact.phoneNumber,
        countryCode: contact.countryCode,
        dialCode: contact.dialCode,
        contactId: contact.existingContactId,
      }));

      // Create task via API (execution is async - user will get notification when done)
      const response = await apiService.createWhatsAppMessageTask({
        contacts,
        message: data.message,
        taskName: data.taskName,
        templateName: data.templateName || 'discuss_ar',
        templateLanguage: data.templateLanguage,
        parameters: data.parameters || [currentBusiness?.name || 'DISLIX', data.message],
        headerAsset: data.headerAsset && data.headerAsset.mediaId
          ? { ...data.headerAsset, mediaId: data.headerAsset.mediaId }
          : data.headerAsset,
        schedule: data.schedule ? {
          enabled: data.schedule.enabled,
          scheduledAt: data.schedule.scheduledAt instanceof Date 
            ? data.schedule.scheduledAt.toISOString() 
            : data.schedule.scheduledAt
        } : undefined,
      });

      if (response.statusCode !== 1000) {
        throw new Error(response.errors?.[0]?.message || 'Failed to create message task');
      }

      setProgress(100);

      // Task is created and will execute asynchronously
      const taskData = response.data;
      const sendResults: SendResult[] = [];

      // Check if contacts were limited (access from response data object)
      const limitedContacts = (response.data as any)?.limitedContacts === true;
      const originalCount = (response.data as any)?.originalContactCount || contacts.length;
      const actualCount = (response.data as any)?.actualContactCount || contacts.length;

      if (taskData?.scheduledAt) {
        // Task was scheduled
        const scheduledDate = new Date(taskData.scheduledAt);
        const message = limitedContacts
          ? `Your message will be sent to ${actualCount} of ${originalCount} contact(s) at ${scheduledDate.toLocaleString()} (limited due to available quota). You will receive a notification when it completes.`
          : `Your message will be sent to ${actualCount} contact(s) at ${scheduledDate.toLocaleString()}. You will receive a notification when it completes.`;
        Alert.alert(
          'Task Scheduled',
          message,
          [{ text: 'OK' }]
        );
      } else {
        // Task created and queued for immediate execution (async)
        const message = limitedContacts
          ? `Your message task has been created for ${actualCount} of ${originalCount} contact(s) (limited due to available quota). It is being processed and you will receive a notification when it completes.`
          : `Your message task has been created and is being processed. You will receive a notification when it completes.`;
        Alert.alert(
          'Task Created',
          message,
          [{ text: 'OK' }]
        );
      }

      // Return all as pending (execution is async)
      data.contacts.forEach(contact => {
        sendResults.push({
          contact,
          success: false, // Pending - will be updated via notifications
          error: undefined,
        });
      });

      setResults(sendResults);
      return sendResults;
    } catch (error: any) {
      console.error('Error creating message task:', error);
      Alert.alert('Error', error.message || 'Failed to create message task');
      
      // Return failed results for all contacts
      return data.contacts.map(contact => ({
        contact,
        success: false,
        error: error.message || 'Failed to create task',
      }));
    } finally {
      setIsSending(false);
    }
  }, [currentBusiness, user, checkMessageLimits]);

  /**
   * Reset sending state
   */
  const resetSendingState = useCallback(() => {
    setIsSending(false);
    setProgress(0);
    setResults([]);
  }, []);

  return {
    isSending,
    progress,
    results,
    sendSingleMessage,
    sendBulkMessages,
    checkMessageLimits,
    resetSendingState,
  };
};

